import React, { memo } from 'react';
import { useTrackVolume, type TrackReference } from '@livekit/components-react';
import { useSpeakingIndicator } from '../../hooks/useSpeakingIndicator.js';
import { VIDEO_CONFIG } from '../../config/videoConfig.js';

interface SpeakingRingProps {
  /** A *subscribed* microphone reference: the publication carries a track */
  microphoneTrackRef: TrackReference;
}

/**
 * The green ring drawn around a tile while its participant is speaking.
 *
 * It measures the microphone on the client (issue #103). LiveKit's
 * `participant.isSpeaking` comes from the server's batched active-speaker
 * updates and flips noticeably after someone starts talking; an analyser on
 * the track itself reacts within one sample.
 *
 * This is its own component so the cost stays contained: `useTrackVolume`
 * sets state 30 times a second, and only this empty overlay re-renders at
 * that rate, not the tile with its video element. The caller mounts it only
 * for an unmuted, subscribed microphone, and unmounting it closes the
 * analyser's audio context. The analyser is receive-only; it is never
 * connected to an output, so it has no effect on what anyone hears.
 */
const SpeakingRingComponent: React.FC<SpeakingRingProps> = ({ microphoneTrackRef }) => {
  const level = useTrackVolume(microphoneTrackRef, VIDEO_CONFIG.speaking.analyser);
  const isSpeaking = useSpeakingIndicator(level);

  // Always mounted and faded with opacity rather than added and removed: a
  // mount/unmount cannot be transitioned, which made the ring snap on and off.
  // It fades in quickly so it still reads as "now", and out slowly so gaps
  // between words do not blink it. It sits *outside* the frame's border
  // (`-inset-1` covers the border-4, then the ring grows outward), matching
  // the look before #103.
  const { fadeInMs, fadeOutMs } = VIDEO_CONFIG.speaking;

  return (
    <div
      aria-hidden="true"
      data-testid="speaking-ring"
      data-speaking={isSpeaking ? 'true' : 'false'}
      className={`pointer-events-none absolute -inset-1 z-10 ${VIDEO_CONFIG.styles.roundedCorners} ring-2 ring-video-speaking transition-opacity ease-out motion-reduce:transition-none`}
      style={{
        opacity: isSpeaking ? 1 : 0,
        transitionDuration: `${isSpeaking ? fadeInMs : fadeOutMs}ms`
      }}
    />
  );
};

export const SpeakingRing = memo(SpeakingRingComponent);
SpeakingRing.displayName = 'SpeakingRing';
